import React, { useMemo } from 'react';
import { Form, Field, FormikProps } from 'formik';
import aOrAn from 'indefinite';

// Material UI
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import SaveIcon from '@material-ui/icons/Save';

// Data
import { SPORT_TERMINOLOGY } from '../../../lib/constants';

// Components
import { FormikDatePicker } from '../../_core/Formik/formik-datepicker.component';
import { StageItemWrapper } from '../assessment-stage.shared-components';
import { IFormOption } from '../../_core/_ui/forms.component';
import { ISessionStageBuilderFormValues } from './session-stage-builder.interface';
import { FormikSelect } from '../../_core/Formik/formik-select.component';
import { PrimaryButton } from '../../_core/_ui/buttons.component';
import { Link } from 'react-router-dom';
import { FormikCheckbox } from '../../_core/Formik/formik-checkbox.component';
import { FormikInput } from '../../_core/Formik/formik-input.component';
import { CUSTOM_DATE_NOTIFICATION_OPTION, EARLY_NOTIFICATION_OPTIONS, FINAL_NOTIFICATION_OPTIONS } from '../assessment-stage.constants';
import Tooltip from '@material-ui/core/Tooltip';
import InfoIcon from '@material-ui/icons/Info';
import { useHasUnsavedFormat, setHasUnsavedFormat } from '../../../lib/stores/assessment-stage.store';
import { useCompanySportTerminology } from '../../../lib/stores/company.store';
import { pluralTerm } from '../../../lib/helpers/term.helper';
import { Sport, SportTerminology } from '../../../lib/types';
import { IAssessmentStageBuilderBaseProps } from '../assessment-stage.types';
import { Label } from '../../_core/_ui/typography.component';
import { IntlMsg } from '../../_core/IntlMsg/intl-msg.component';
import { messages } from './session-stage-builder.messages';

interface ISessionStageBuilder extends IAssessmentStageBuilderBaseProps {
  form: FormikProps<ISessionStageBuilderFormValues>;
  stage: IAssessmentStage;
  drillOptions: IFormOption[];
  practicePlanOptions: IFormOption[];
  // Hides the association-specific RSVP toggle, the fixed custom-date reminder and the
  // seed-owned session limit; the container omits those keys from the payload under the same flag.
  catalogSettingsMode?: boolean;
  // Gates the overlap setting's render and its payload key — a first stage has no previous
  // stage to overlap. See "Render visibility and data visibility are one decision".
  followsSessionStage?: boolean;
}

export const SessionStageBuilder: React.FC<ISessionStageBuilder> = ({
  form,
  stage,
  drillOptions,
  practicePlanOptions,
  catalogSettingsMode,
  followsSessionStage,
  style
}) => {
  const termLookup = useCompanySportTerminology();
  const eventTerm = termLookup[SportTerminology.EVENT]
  const sessionTerm = termLookup[SportTerminology.SESSION].toLowerCase()

  const hasUnsavedFormat = useHasUnsavedFormat();
  const { dirty, values } = form;

  const showCustomDate = useMemo(() => values.earlyReminder && values.earlyReminder.value === CUSTOM_DATE_NOTIFICATION_OPTION.value, [values.earlyReminder])
  // A catalog template applies at an arbitrary future date, so the fixed-calendar-date option
  // only exists outside catalog mode. Not memoized: cheap filter over 8 entries, not a hook dep.
  const earlyReminderOptions = catalogSettingsMode
    ? EARLY_NOTIFICATION_OPTIONS.filter(option => option.value !== CUSTOM_DATE_NOTIFICATION_OPTION.value)
    : EARLY_NOTIFICATION_OPTIONS;

  if (dirty && !hasUnsavedFormat) {
    setHasUnsavedFormat(true);
  } else if (!dirty && hasUnsavedFormat) {
    setHasUnsavedFormat(false);
  }

  if (!drillOptions.length) return (
    <Box p={2}>
      <Typography>{pluralTerm(eventTerm)} are required to configure an {sessionTerm}. <Link to={"/" + pluralTerm(SPORT_TERMINOLOGY[Sport.default][SportTerminology.EVENT])}>Create one here</Link></Typography>
    </Box>
  );

  return (
    <Form>
      <StageItemWrapper style={style}>
        <Box display="flex" flexDirection="column">
          <Box display="flex" alignItems="center">
            <Typography gutterBottom><IntlMsg {...messages.evaluatePracticePlans} /></Typography>
            <Box mr={2}>
              <Tooltip title={`Practice plans can be assigned when groups are approved, or manually by editing ${aOrAn(sessionTerm)} after scheduling`}>
                <InfoIcon fontSize="small" style={{ marginLeft: '8px' }} />
              </Tooltip>
            </Box>
          </Box>
          {practicePlanOptions.length > 0 ? (
            <Field
              fid={`${stage._id}_${stage.order}`}
              name="practicePlans"
              options={practicePlanOptions}
              component={FormikSelect}
              isMulti
              disabled={stage.isCompleted}
            />
          ) : (
            // Plans are optional, so an empty list gets the drills hint rather than the hard
            // early return above — hiding the field made the setting look unsupported entirely.
            <Typography variant="body2" color="textSecondary">
              <IntlMsg {...messages.noPracticePlans} />{' '}
              <Link to="/practice-plans"><IntlMsg {...messages.createPracticePlan} /></Link>
            </Typography>
          )}
          <Box mt={1}>
            <Box display="flex" alignItems="center">
              <Typography gutterBottom><IntlMsg {...messages.evaluateEvents} values={{ events: pluralTerm(eventTerm).toLowerCase() }} /></Typography>
              <Box mr={2}>
                <Tooltip title={eventTerm + ` can be assigned when groups are approved, or manually by editing an ${aOrAn(sessionTerm)} after scheduling`}>
                  <InfoIcon fontSize="small" style={{ marginLeft: '8px' }} />
                </Tooltip>
              </Box>
            </Box>

            <Field
              fid={`${stage._id}_${stage.order}`}
              name="drills"
              options={drillOptions}
              component={FormikSelect}
              isMulti
              disabled={stage.isCompleted}
            />
          </Box>
          {!catalogSettingsMode && (
            <Box display="flex" flexDirection="column" mt={2}>
              <Label style={{ fontWeight: 'bold' }}>
                <IntlMsg {...messages.limitSessionsLabel} values={{ sessions: pluralTerm(sessionTerm) }} />
              </Label>
              <Field
                autoWidth
                margin="none"
                name="limitTracks"
                fid={`${stage._id}_${stage.order}`}
                component={FormikCheckbox}
                label={<IntlMsg {...messages.limitSessionsHelp} values={{ session: sessionTerm }} />}
                disabled={stage.isCompleted}
              />
              {values.limitTracks && (
                <Box width="160px">
                  <Field
                    fid={`${stage._id}_${stage.order}`}
                    name="tracks"
                    type="number"
                    min={1}
                    step={1}
                    component={FormikInput}
                    margin="none"
                    label={{ ...messages.maxSessionsLabel, values: { sessions: pluralTerm(sessionTerm) } }}
                    disabled={stage.isCompleted}
                  />
                </Box>
              )}
            </Box>
          )}
          <Box display="flex" flexDirection="column" mt={2}>
            <Label style={{ fontWeight: 'bold' }}>Notify players of assignment</Label>
            <Field
              autoWidth
              margin="none"
              name="enableNotifications"
              fid={`${stage._id}_${stage.order}`}
              component={FormikCheckbox}
              label={`Send an email & text immediately after being assigned to this ${sessionTerm} letting all ${pluralTerm(termLookup[SportTerminology.ATHLETE]).toLowerCase()} know they've been scheduled.`}
              disabled={stage.isCompleted}
            />
          </Box>
          {!catalogSettingsMode && (
            <Box display="flex" flexDirection="column" mt={2}>
              <Label style={{ fontWeight: 'bold' }}>Request attendance RSVPs</Label>
              <Field
                autoWidth
                margin="none"
                name="requestAttendance"
                fid={`${stage._id}_${stage.order}`}
                component={FormikCheckbox}
                label={`Players will receive a link to confirm attendance via email or SMS for each ${sessionTerm} generated from this stage.`}
                disabled={stage.isCompleted}
              />
            </Box>
          )}
          {followsSessionStage && (
            <Box display="flex" flexDirection="column" mt={2}>
              <Label style={{ fontWeight: 'bold' }}>
                <IntlMsg {...messages.allowOverlapLabel} values={{ sessions: pluralTerm(termLookup[SportTerminology.SESSION]) }} />
              </Label>
              <Field
                autoWidth
                margin="none"
                name="allowOverlappingSessions"
                fid={`${stage._id}_${stage.order}`}
                component={FormikCheckbox}
                label={<IntlMsg
                  {...messages.allowOverlapHelp}
                  values={{
                    aSession: aOrAn(sessionTerm),
                    field: termLookup[SportTerminology.FIELD].toLowerCase(),
                    fields: pluralTerm(termLookup[SportTerminology.FIELD]).toLowerCase()
                  }}
                />}
                disabled={stage.isCompleted}
              />
            </Box>
          )}
          <Box display="flex" flexDirection="column" mt={2}>
            <Label style={{ fontWeight: 'bold' }}>Send reminders closer to the ice time</Label>
            <Field
              autoWidth
              margin="none"
              name="enableReminders"
              fid={`${stage._id}_${stage.order}`}
              component={FormikCheckbox}
              label={`Send email & text reminders to all ${pluralTerm(termLookup[SportTerminology.ATHLETE]).toLowerCase()} assigned to this ${sessionTerm} before the scheduled ${sessionTerm}.`}
              disabled={stage.isCompleted}
            />
          </Box>
          {values.enableReminders && (<>
            <Box display="flex" alignItems="center">
              <Field
                fid={`${stage._id}_${stage.order}`}
                name="earlyReminder"
                options={earlyReminderOptions}
                component={FormikSelect}
                disabled={stage.isCompleted}
                label={`${sessionTerm.charAt(0).toUpperCase() + sessionTerm.substring(1)} Reminder #1`}
                isClearable
              />
            </Box>
            {!catalogSettingsMode && showCustomDate &&
              <Box display="flex" alignItems="start" mb={2}>
                <Field
                  autoWidth
                  margin="none"
                  name="customNotificationDate"
                  fid={`${stage._id}_${stage.order}`}
                  component={FormikDatePicker}
                  label={"Custom notification date"}
                  disabled={stage.isCompleted}
                  dateFormat="YYYY/MM/DD h:mm A"
                  datetime
                />
              </Box>
            }
            <Box display="flex" alignItems="center">
              <Field
                fid={`${stage._id}_${stage.order}`}
                name="finalReminder"
                options={FINAL_NOTIFICATION_OPTIONS}
                component={FormikSelect}
                disabled={stage.isCompleted}
                label="Final Reminder"
                isClearable
              />
            </Box>
          </>)}
        </Box>
        {!stage.isCompleted && dirty && <Box mt={2}>
          <PrimaryButton type="submit" startIcon={<SaveIcon />}>
            Save Changes
          </PrimaryButton>
        </Box>}
      </StageItemWrapper>
    </Form>
  );
};

